import LocalShippingOutlined from "@mui/icons-material/LocalShippingOutlined";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { formatAddress } from "encamp-shared/src/utils/address";
import { compareByTransporterLineNumber } from "encamp-shared/src/waste/manifestSourcePriority";
import { WasteManifestSource } from "generated-graphql/graphql";

import { SOURCE_LABEL } from "../manifestSourceDisplay";
import { SourceLetterBox } from "../SourceLetterBox";
import { ManifestPartyCard } from "./ManifestPartyCard";
import { ManifestDetail, ManifestTransporter } from "./types";

interface TransportersSectionProps {
  transporters: NonNullable<ManifestDetail["wasteTransporters"]>;
  /**
   * The manifest's primary source. Each transporter shows a source mark only
   * when its row came from a different source — mirroring how SourcedField
   * marks fallback values elsewhere in the Manifest data card. (The API tags
   * every row with its pillar; an untagged row is treated as primary-sourced,
   * i.e. unmarked, rather than guessing a pillar.)
   */
  primarySource: WasteManifestSource;
}

function daysInPossessionLabel(transporter: ManifestTransporter): string | undefined {
  if (transporter.daysInPossession == null) return undefined;
  const days = transporter.daysInPossession;
  return `${days} ${days === 1 ? "day" : "days"} in possession`;
}

/**
 * Transporters card: the middle leg of the Generator → Transporters →
 * Receiving flow. Each entry is a compact name / EPA ID / days-in-possession
 * block (1–N transporters, rarely more than 5) rather than a label/value grid,
 * so the card stays narrow between the two party cards.
 */
export function TransportersSection({ transporters, primarySource }: TransportersSectionProps) {
  if (transporters.length === 0) return null;

  // The API already returns the merged list sorted; re-sort defensively with
  // the shared comparator in case a caller passes an unsorted list.
  const sorted = [...transporters].sort(compareByTransporterLineNumber);

  return (
    <ManifestPartyCard title="Transporters" icon={<LocalShippingOutlined fontSize="small" />}>
      <Stack spacing={1.25} divider={<Divider flexItem />}>
        {sorted.map((transporter) => {
          const detailLine = [transporter.transporterId, daysInPossessionLabel(transporter)]
            .filter(Boolean)
            .join(" · ");
          // Only vendor-ingested rows ever carry an address; shown unlabeled
          // under the EPA ID line when present.
          const address = formatAddress(
            [transporter.vendorAddressLine1, transporter.vendorAddressLine2]
              .filter(Boolean)
              .join(" "),
            transporter.vendorCity,
            transporter.vendorState,
            transporter.vendorZip
          );
          const rowSource = transporter.source ?? primarySource;
          const isFallback = rowSource !== primarySource;
          return (
            <Stack
              key={transporter.transporterLineNumber}
              direction="row"
              spacing={1}
              alignItems="baseline"
            >
              <Typography variant="body2" color="text.secondary">
                {transporter.transporterLineNumber}.
              </Typography>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                >
                  {transporter.transporterName ?? "—"}
                  {isFallback && (
                    <SourceLetterBox
                      source={rowSource}
                      tooltip={`Data source: ${SOURCE_LABEL[rowSource]}`}
                    />
                  )}
                </Typography>
                {detailLine && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {detailLine}
                  </Typography>
                )}
                {address && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {address}
                  </Typography>
                )}
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </ManifestPartyCard>
  );
}
